import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DetectMethodChanges } from '../utils';
import { anyKeyWithPrefix, ParamChangeMap } from './opg-copilot-param-diff';
import {
    isDualSideInputs,
    OpgAtmOffsetSub,
    OpgDateSelection,
    OpgMaxRisk,
    OpgPriceRange,
    OpgSide,
    Run,
    SideInputs,
    TabInputs,
    TabKind
} from './opg-copilot-domain';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Typography constants (literal UTF-8, as throughout the copilot family sources).
const GE = '≥';         // >=
const EN_DASH = '–';    // range dash
const MINUS = '−';      // proper minus sign
const ARROW_UP = '↑';
const ARROW_DOWN = '↓';
const SEP = ' · ';      // the family's "Param · Value" chip separator (APG/Hedge idiom)

// One rendered chip of the collapsed questionnaire summary.
export interface OpgBreadcrumbChip {
    key: string;         // unique data-element value, e.g. 'chip-date', 'chip-calls-max-risk'
    text: string;
    normalized: boolean; // renders the small "N" badge (the sent Normalized flag)
    stale?: boolean;     // WI-15: this chip shows a CAPTURED grid value the panel has since moved away from
    changed?: boolean;   // CR-20260831-004: a field this chip summarises changed since the tab's last run
}

// Chip row summarising the SENT questionnaire inputs after a Run collapses the form
// (spec §6: "the questionnaire collapses to a summary strip while results show").
// Clicking ANYWHERE on the strip asks the orchestrator to re-expand the questionnaire —
// the whole bar is one click target (family recipe ba9f56a40, mirrors the Hedge
// input-summary and the APG breadcrumb-bar). Pure display: no mutation, no judgement —
// it reflects exactly what was sent (courier, §6).
//
// EVERY chip is self-describing "Param · Value" (BF-4/A4 — owner: "they have to be
// descriptive... it's parameter dot value"): the Param is the questionnaire's own
// fieldset legend (Date / Price Range / Minimum Profit / Max Risk) and
// dates render in the family format "Fri 10-Jul-26" (the APG breadcrumb's DOW3 dd-MMM-yy).
//
// Chip order: date first, then per side (both sides on the Calls & Puts tab, each chip
// prefixed "C: " / "P: " — call and put parameters are never blended, §10 spirit):
// price range, min profit, max risk. Risk chips render the domain level
// word verbatim (Low | Moderate | Aggressive | All — never High/Conservative/Auto, §10).
@Component({
    selector: 'ets-opg-copilot-breadcrumb-bar',
    templateUrl: './opg-copilot-breadcrumb-bar.component.html',
    styleUrls: ['./opg-copilot-breadcrumb-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgCopilotBreadcrumbBarComponent {
    constructor(private _changeDetector: ChangeDetectorRef) {
    }

    // Contract inputs (set by the orchestrator; tabKind identifies which tab's sent
    // inputs are being summarised — the dual-vs-single SHAPE is discriminated via
    // isDualSideInputs, never guessed from the tab).
    @Input() tabKind: TabKind;

    @Input() inputs: TabInputs | null = null;

    // Reserved by the WB-3 contract sheet (integrators may bind [run]); the chips derive
    // everything from `inputs` today, so it is intentionally unused.
    @Input() run: Run | null = null;

    // The panel's currently-loaded date, family-formatted by the orchestrator — the text for the
    // 'loaded' Date chip (WI-3). The sent inputs carry the UI-only 'loaded' marker (never a date),
    // so the chip's date is resolved here at display time; null when the panel has no date.
    @Input() loadedDateText: string | null = null;

    // WI-15/A6: the panel's expiration has drifted from what the CoPilot captured, so loadedDateText
    // above is the CAPTURED (frozen) date. Only the 'loaded' Date chip may show that yellow — a
    // Single/Range date chip is the user's OWN typed date and reads nothing from the grid, so it can
    // never be stale.
    @Input() staleLoadedDate = false;

    // CR-20260831-004: the tab's changed-vs-last-run key map (full tab grammar — 'date.*',
    // 'calls.*'/'puts.*'/'side.*'; see opg-copilot-param-diff.ts). The bar renders the LIVE
    // working inputs, so a changed chip shows the new value with the yellow "does not match the
    // results" tint. Fresh object per recompute (OnPush wake-up); null/empty = clean.
    @Input() changedKeys: ParamChangeMap | null = null;

    @Output() expandRequested = new EventEmitter<void>();

    get hasAny(): boolean {
        return this.chips.length > 0;
    }

    get chips(): OpgBreadcrumbChip[] {
        const inp = this.inputs;

        if (!inp) {
            return [];
        }

        const list: OpgBreadcrumbChip[] = [];

        const dateText = this.formatDateSelection(inp.date);
        if (dateText) {
            const staleDate = this.staleLoadedDate && !!inp.date && inp.date.kind === 'loaded';
            list.push({key: 'chip-date', text: 'Date' + SEP + dateText, normalized: false, stale: staleDate,
                changed: anyKeyWithPrefix(this.changedKeys, ['date.'])});
        }

        // CR-20260727-002: the SIDE now reaches the chip builder, because a follow-through chip's
        // arrow is derived from it rather than read off a (now deleted) priceRange.direction. The
        // dual tab knows both sides explicitly; a single-side tab's side IS its tabKind, which is
        // already a contract @Input. The dual-vs-single SHAPE still comes from isDualSideInputs,
        // never from the tab (that rule is unchanged) — tabKind is only consulted for the side name.
        if (isDualSideInputs(inp)) {
            this.pushSideChips(list, inp.calls, 'C: ', 'calls-', 'calls', 'calls.');
            this.pushSideChips(list, inp.puts, 'P: ', 'puts-', 'puts', 'puts.');
        } else {
            this.pushSideChips(list, inp.side, '', '', this.tabKind === 'puts' ? 'puts' : 'calls', 'side.');
        }

        return list;
    }

    // User-driven only: the whole bar is the click target that requests expansion.
    @DetectMethodChanges()
    onBarClicked(): void {
        this.expandRequested.emit();
    }

    trackByKey(index: number, chip: OpgBreadcrumbChip): string {
        return chip.key;
    }

    // ---- chip builders (display formatting only) ------------------------------------

    // modelPrefix = this side's key prefix in changedKeys ('calls.' / 'puts.' / 'side.') —
    // a chip collapses several diff keys, so ANY hit under its family flags it (CR-20260831-004).
    private pushSideChips(list: OpgBreadcrumbChip[], side: SideInputs, prefix: string, keyPrefix: string,
                          sideKind: OpgSide, modelPrefix: string): void {
        if (!side) {
            return;
        }

        if (side.mode === 'preset') {
            list.push({key: 'chip-' + keyPrefix + 'preset', text: prefix + 'Preset' + SEP +
                (side.presetName || (side.presetId ? 'Unavailable Preset' : 'Select Preset')) + SEP + 'Anchor ' + side.presetAnchor,
                normalized: false, changed: anyKeyWithPrefix(this.changedKeys,
                    [modelPrefix + 'mode', modelPrefix + 'presetId', modelPrefix + 'presetAnchor'])});
            return;
        }
        const pr = this.formatPriceRange(side.priceRange, sideKind);
        if (pr) {
            list.push({key: 'chip-' + keyPrefix + 'price-range', text: prefix + 'Price Range' + SEP + pr, normalized: false,
                changed: anyKeyWithPrefix(this.changedKeys, [modelPrefix + 'priceRange',
                    modelPrefix + 'startStrike', modelPrefix + 'endStrike', modelPrefix + 'atm.'])});
        }

        if (side.minProfit && this.isFiniteNumber(side.minProfit.amount)) {
            list.push({
                key: 'chip-' + keyPrefix + 'min-profit',
                text: prefix + 'Minimum Profit' + SEP + GE + '$' + this.formatMoney(side.minProfit.amount),
                normalized: !!side.minProfit.normalized,
                changed: anyKeyWithPrefix(this.changedKeys, [modelPrefix + 'minProfit.'])
            });
        }

        const mr = this.formatMaxRisk(side.maxRisk);
        if (mr) {
            list.push({key: 'chip-' + keyPrefix + 'max-risk', text: prefix + 'Max Risk' + SEP + mr.text, normalized: mr.normalized,
                changed: anyKeyWithPrefix(this.changedKeys, [modelPrefix + 'maxRisk.'])});
        }
    }

    // "7510–7550" | "7520 ↑ FT" | "Chosen ATM −$30 · Range $50" | "7630 to Expected Range"
    // (WI-4: atmOffset renders the anchor word, ONE signed dollar offset — − toward the money /
    // + further out — then the secondary sub via formatAtmSub below;
    // CR-20260715-001: expectedRange renders the start strike + plain words — no direction
    // glyph, the side implies it; plain words per house rule).
    private formatPriceRange(pr: OpgPriceRange, sideKind: OpgSide): string | null {
        if (!pr) {
            return null;
        }
        switch (pr.kind) {
            case 'strikes':
                return pr.startStrike + EN_DASH + pr.endStrike;
            case 'followThrough':
                // CR-20260727-002: the glyph comes from the SIDE. It used to come from
                // pr.direction, which meant a puts side that had been left on "Up" printed a chip
                // claiming upward coverage while the engine ran downward — the chip could
                // contradict the run it was summarising. Derived from the side it cannot.
                return pr.startStrike + ' ' + (sideKind === 'puts' ? ARROW_DOWN : ARROW_UP) + ' FT';
            case 'expectedRange':
                return pr.startStrike + ' to Expected Range';
            case 'atmOffset': {
                const anchorWord = pr.anchor === 'market' ? 'Market ATM ' : 'Chosen ATM ';
                const offsetText = pr.offset < 0
                    ? MINUS + '$' + Math.abs(pr.offset)
                    : (pr.offset > 0 ? '+$' + pr.offset : '$0');
                return anchorWord + offsetText + this.formatAtmSub(pr.sub);
            }
            default:
                return null;
        }
    }

    // The Distance-To-Cover sub (Both CR): "· Range $50" | "· Range · Expected Range" |
    // "· Both $50" | "· Both · Expected Range" | "· Follow-Through". The bounded kinds carry
    // their value; the dynamic value renders the CR-20260715-001 chip wording ("Expected
    // Range") so the phrase survives its move from a third button into the checkbox.
    private formatAtmSub(sub: OpgAtmOffsetSub): string {
        if (sub && (sub.kind === 'range' || sub.kind === 'both')) {
            const word = sub.kind === 'both' ? 'Both' : 'Range';
            const v = sub.value;
            if (v && v.mode === 'expectedRange') {
                return SEP + word + SEP + 'Expected Range';
            }
            return SEP + word + ' $' + ((v && v.mode === 'dollar') ? v.extent : NaN);
        }
        return SEP + 'Follow-Through';
    }

    // "$1,000" (dollar, N badge when normalized) or the level word verbatim
    // (Low | Moderate | Aggressive | All — the domain literal, §10).
    private formatMaxRisk(mr: OpgMaxRisk): { text: string; normalized: boolean } | null {
        if (!mr) {
            return null;
        }
        if (mr.kind === 'dollar') {
            if (!this.isFiniteNumber(mr.amount)) {
                return null;
            }
            return {text: '$' + this.formatMoney(mr.amount), normalized: !!mr.normalized};
        }
        if (mr.kind === 'relative') {
            return mr.level ? {text: mr.level, normalized: false} : null;
        }
        return null;
    }

    // Family date format (BF-4/A4 — the APG breadcrumb's fmtDow, duplicated per the
    // isolation rule): single "Fri 10-Jul-26"; range "Fri 10-Jul-26 – Fri 17-Jul-26"
    // (full–full, no compression — exactly what the APG bar renders).
    private formatDateSelection(sel: OpgDateSelection): string | null {
        if (!sel) {
            return null;
        }
        if (sel.kind === 'loaded') {
            return this.loadedDateText || null;   // resolved by the orchestrator (the marker carries no date)
        }
        if (sel.kind === 'single') {
            return this.isValidDate(sel.date) ? this.formatDowDate(sel.date) : null;
        }
        if (sel.kind === 'range') {
            if (!this.isValidDate(sel.start) || !this.isValidDate(sel.end)) {
                return null;
            }
            return this.formatDowDate(sel.start) + ' ' + EN_DASH + ' ' + this.formatDowDate(sel.end);
        }
        return null;
    }

    // ---- primitives ------------------------------------------------------------------

    private formatDowDate(d: Date): string {
        return DOW3[d.getDay()] + ' '
            + this.pad2(d.getDate()) + '-' + MONTHS[d.getMonth()] + '-' + this.pad2(d.getFullYear() % 100);
    }

    private pad2(n: number): string {
        return n < 10 ? '0' + n : '' + n;
    }

    private isValidDate(d: Date): boolean {
        return d instanceof Date && !isNaN(d.getTime());
    }

    private isFiniteNumber(n: number): boolean {
        return typeof n === 'number' && isFinite(n);
    }

    // Thousands separators, no rounding: 1000 -> "1,000"; 1234.5 -> "1,234.5".
    private formatMoney(n: number): string {
        const negative = n < 0;
        const abs = Math.abs(n);
        const parts = ('' + abs).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return (negative ? MINUS : '') + parts.join('.');
    }
}
